import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import '../App.css';
import ListingsMap from '../components/ListingsMap';

/** A robust <img> component that shows a fallback placeholder on error. */
function PropertyImage({ src, alt }) {
  const [hasError, setHasError] = useState(false);

  // When the src prop changes, reset the error state.
  useEffect(() => {
    setHasError(false);
  }, [src]);

  const placeholder = 'https://placehold.co/400x300/f0f0f0/ccc?text=No+Image+Available';
  const finalSrc = hasError ? placeholder : src || placeholder;

  return (
    <img
      src={finalSrc}
      alt={alt}
      style={{
        width: '100%',
        height: '200px',
        objectFit: 'cover',
        borderRadius: 4,
        background: '#f0f0f0',
      }}
      onError={() => setHasError(true)}
    />
  );
}

export default function Listings() {
  const [properties, setProperties]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const location                      = useLocation();
  const [hoveredPropertyId, setHoveredPropertyId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'

  const handleToggle = () => setViewMode(viewMode === 'grid' ? 'map' : 'grid');

  /* ───────── fetch listings whenever query-string changes ───────── */
  useEffect(() => {
    const qs = new URLSearchParams(location.search);

    setLoading(true);
    setError(null);

    fetch(`https://kaushal.calisearch.org/fetch_listings.php?${qs}`)
      .then((r) => (r.ok ? r.json() :
        Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((rows) => {
        setProperties(rows);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [location.search]);

  /* ───────── load images directly from URLs stored in database ───────── */
  const buildImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    // Load images directly from the URLs stored in the database
    return photoUrl;
  };

  /* ───────── UI states ───────── */
  if (loading)
    return (
      <div className="listings-container">
        <h2>Property Listings</h2>
        <p style={{ padding: '2rem' }}>Loading properties …</p>
      </div>
    );

  if (error)
    return (
      <div className="listings-container">
        <h2>Property Listings</h2>
        <p style={{ color: 'red' }}>Error: {error}</p>
      </div>
    );

  if (!properties.length)
    return (
      <div className="listings-container">
        <h2>Property Listings</h2>
        <p style={{ padding: '2rem' }}>No properties match your filters.</p>
      </div>
    );

  /* ───────── normal render ───────── */
  return (
    <div className="listings-container">
      <h2>Property Listings ({properties.length} found)</h2>
      <div className="toggle-switch" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 16 }}>
        <span style={{ marginRight: 8 }}>Grid</span>
        <label className="switch">
          <input type="checkbox" checked={viewMode === 'map'} onChange={handleToggle} />
          <span className="slider"></span>
        </label>
        <span style={{ marginLeft: 8 }}>Map</span>
      </div>
      {viewMode === 'map' ? (
        <ListingsMap
          listings={properties}
          apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyB0szWlIt9Vj26cM300wTcWxwL0ABHZ9HE"}
          hoveredPropertyId={hoveredPropertyId}
          setHoveredPropertyId={setHoveredPropertyId}
        />
      ) : (
      <div className="grid">
          {properties.map((p) => (
            <Link
              to={`/property/${p.id}`}
              key={p.id}
              style={{ textDecoration: 'none', color: 'inherit' }}
              onMouseEnter={() => setHoveredPropertyId(p.id)}
              onMouseLeave={() => setHoveredPropertyId(null)}
            >
              <div className={`card${hoveredPropertyId === p.id ? ' highlighted' : ''}`}>
                <PropertyImage
                  src={buildImageUrl(p.PhotoURL)}
                  alt={`Photo for ${p.FullAddress}`}
                />
                <div className="card-body">
                  <h3 className="card-price">
                    ${parseFloat(p.ListPrice || 0).toLocaleString()}
                  </h3>
                  <p className="card-details">
                    {p.Beds} bd<span className="card-separator">|</span>
                    {p.Baths} ba<span className="card-separator">|</span>
                    {p.SqFt} sqft
                  </p>
                  <p className="card-address">{p.FullAddress}</p>
                  <p className="card-city">
                    {p.City}, CA&nbsp;{p.ZIP}
                  </p>
                </div>
          </div>
            </Link>
        ))}
      </div>
      )}
    </div>
  );
}
